import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { foyerApi, type InviteLookup } from "../http/foyer";
import { useAuth } from "../auth/AuthContext";

/**
 * Public landing page for the invite-acceptance flow. Looks up the
 * token, lets the user pick a password, redeems the invite (which
 * creates their User), then logs them in and bounces to the CMS root.
 */
export default function AcceptInvitePage() {
  const { token = "" } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(["users", "common"]);
  const { login } = useAuth();
  const [lookup, setLookup] = useState<InviteLookup | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    foyerApi
      .lookupInvite(token)
      .then((data) => {
        if (!cancelled) setLookup(data);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function submit() {
    if (!lookup) return;
    setErr(null);
    if (password.length < 8) {
      setErr(t("acceptInvitePage.passwordTooShort"));
      return;
    }
    if (password !== confirm) {
      setErr(t("acceptInvitePage.passwordsMismatch"));
      return;
    }
    setBusy(true);
    try {
      await foyerApi.redeemInvite(token, password);
      await login(lookup.email, password);
      navigate("/", { replace: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (err && !lookup) {
    return (
      <Box
        sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 4 }}
      >
        <Paper sx={{ p: 4, maxWidth: 480 }}>
          <Typography variant="h6" gutterBottom>
            {t("acceptInvitePage.cannotUse")}
          </Typography>
          <Alert severity="error">{err}</Alert>
          <Button
            sx={{ mt: 3 }}
            variant="outlined"
            onClick={() => navigate("/login")}
          >
            {t("acceptInvitePage.goToSignIn")}
          </Button>
        </Paper>
      </Box>
    );
  }

  if (!lookup) return null;

  return (
    <Box
      sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 4 }}
    >
      <Paper
        sx={{ p: 4, maxWidth: 480, width: "100%" }}
        data-testid="accept-invite"
      >
        <Typography variant="h5" gutterBottom>
          {t("acceptInvitePage.welcome")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          <Trans
            t={t}
            i18nKey="acceptInvitePage.invited"
            values={{ invitedBy: lookup.invitedBy, email: lookup.email }}
            components={{ strong: <strong /> }}
          />
        </Typography>
        <Stack spacing={2}>
          <TextField
            label={t("common:labels.email")}
            value={lookup.email}
            disabled
            size="small"
          />
          <TextField
            label={t("common:labels.password")}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            size="small"
            autoFocus
            inputProps={{ "data-testid": "accept-password" }}
          />
          <TextField
            label={t("common:labels.confirmPassword")}
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            size="small"
            inputProps={{ "data-testid": "accept-confirm" }}
          />
          {err && <Alert severity="error">{err}</Alert>}
          <Button
            variant="contained"
            onClick={submit}
            disabled={busy || !password || !confirm}
            data-testid="accept-submit"
          >
            {busy
              ? t("acceptInvitePage.settingUp")
              : t("acceptInvitePage.submit")}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
